"use client";

import { trpcApi } from "@lattice/frontend/lib/trpc/client/index";
import StockDashboardTabs from "@lattice/frontend/components/stock-dashboard/stock-dashboard-tabs";
import { Skeleton } from "@lattice/frontend/components/ui/skeleton";
import ExclusionTags from "./exclusion-tags";

export default function StockPageLayoutClient({
  stock_symbol,
  children,
}: {
  stock_symbol: string;
  children: React.ReactNode;
}) {
  const [details] = trpcApi.stock.get_details.useSuspenseQuery({
    symbol: stock_symbol,
  });

  return (
    <div className="w-full bg-background">
      <div className="w-full p-2 sm:p-3 lg:p-4 bg-background">
        <div className="w-full max-w-7xl mx-auto bg-background">
          <div className="sticky top-0 bg-background/95 backdrop-blur-md z-50 pt-4 sm:pt-6 pb-4 px-4 sm:px-6">
            <div className="flex justify-between w-full items-center gap-4">
              {details ? (
                <div className="grid grid-cols-[1fr_auto] gap-4 items-center">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="w-16 h-16 shrink-0">
                      {details.image ? (
                        <img
                          className="w-16 h-16"
                          alt={`${details.name} Logo`}
                          src={`data:image/png;base64,${details.image}`}
                        />
                      ) : (
                        <div className="bg-secondary text-secondary-foreground font-bold w-16 h-16 rounded-lg flex items-center justify-center text-4xl">
                          {details.name.charAt(0).toUpperCase()}
                        </div>
                      )}
                    </div>

                    <div className="flex flex-col gap-2 min-w-0 flex-1">
                      <div className="flex items-center gap-3 flex-wrap min-w-0">
                        <h1 className="font-sans font-bold ml-2.5 text-sm sm:text-md md:text-2xl lg:text-3xl leading-tight truncate text-white/90">
                          {details.name
                            .replace(/\s+Common\s+Stock$/i, "")
                            .replace(/\s+Capital\s+Stock$/i, "")
                            .replace(/\s+Stock$/i, "")}
                        </h1>
                        <div className="px-2.5 py-0.5 rounded-lg bg-linear-to-r from-purple-500/10 to-blue-500/10 backdrop-blur-sm border border-purple-500/10 shadow-sm shrink-0">
                          <span className="text-xs font-semibold uppercase tracking-wider bg-linear-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
                            {stock_symbol}
                          </span>
                        </div>
                      </div>

                      {/* Exclusion tags */}
                      <div className="min-w-0 ml-2.5 mt-1">
                        <ExclusionTags symbol={stock_symbol} />
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-5">
                  <Skeleton className="w-20 h-20 rounded-lg" />
                  <div className="flex flex-col items-start justify-between h-20 relative">
                    <div
                      className="flex items-end gap-3 ml-2 absolute top-1/2 -translate-y-1/2"
                      style={{ marginTop: "-12px" }}
                    >
                      <Skeleton className="h-8 w-48" />
                      <Skeleton className="h-6 w-16 rounded-lg" />
                    </div>
                    <div className="self-end w-full flex justify-start pt-14">
                      <div className="gap-2 hidden sm:flex">
                        <Skeleton className="h-6 w-20 rounded-full" />
                        <Skeleton className="h-6 w-24 rounded-full" />
                        <Skeleton className="h-6 w-20 rounded-full" />
                        <Skeleton className="h-6 w-28 rounded-full" />
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div className="my-auto">
                <StockDashboardTabs
                  symbol={stock_symbol}
                  is_etf={details?.is_etf ?? false}
                  is_crypto={details?.is_crypto ?? false}
                />
              </div>
            </div>
          </div>

          {children}
        </div>
      </div>
    </div>
  );
}
